import {
  FuturesCalculatedCost,
  getFormatter,
  InstrumentCategoryEnum,
  isDefined,
  TradeType,
  useLoadableState,
} from '@eih-netbroker/common';
import { useCallback, useMemo } from 'react';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { ErrorWidgetContent } from '../../../../shared/components/error-widget-content/ErrorWidgetContent';
import { NbLoader } from '../../../../shared/components/loader/Loader';
import { ValidatedOrder } from '../../services/order/useNormalOrderView';
import { useOrderConfirmCalculateCost } from '../../services/order/useOrderConfirmCalculateCost';
import { useOrderState } from '../OrderStateContext';
import classes from './OrderConfirmCalculatedCostView.module.scss';
import { OrderConfirmRow } from './OrderConfirmRow';

interface OrderConfirmCalculatedCostViewProps {
  order: ValidatedOrder;
}

const unknownValue = '-';

export const OrderConfirmCalculatedCostView = ({ order }: OrderConfirmCalculatedCostViewProps) => {
  const { translation } = useLocale();
  const { formatCurrency } = getFormatter();

  const { isCostCalculated, calculatedCostState, getOrderTotalPrice, getFuturesOrderTransactionInfo } = useOrderState();
  const { calculateOrderCost } = useOrderConfirmCalculateCost();

  const calculatedCost = useMemo(() => {
    if (calculatedCostState && calculatedCostState.isLoaded) {
      if (calculatedCostState.calculatedCost.tradeType === TradeType.Buy) {
        return {
          ...calculatedCostState.calculatedCost,
          amount: calculatedCostState.calculatedCost.buyAmount,
        };
      } else {
        return {
          ...calculatedCostState.calculatedCost,
          amount: calculatedCostState.calculatedCost.sellAmount,
        };
      }
    }
  }, [calculatedCostState]);

  const totalPrice = useLoadableState(async () => {
    if (order.instrument.instrumentCategory === InstrumentCategoryEnum.Futures) {
      return undefined;
    }

    if (order.instrument) {
      return {
        value: await getOrderTotalPrice(order),
      };
    }
  }, [order, getOrderTotalPrice]);

  const futuresValues = useLoadableState<FuturesCalculatedCost | undefined>(async () => {
    if (order.instrument.instrumentCategory !== InstrumentCategoryEnum.Futures) {
      return undefined;
    }

    return getFuturesOrderTransactionInfo(order);
  }, [getFuturesOrderTransactionInfo, order]);

  const transactionSum = useMemo(() => {
    if (!calculatedCost) {
      return undefined;
    }

    const signedCommission = order.tradeType === TradeType.Buy ? calculatedCost.amount : -calculatedCost.amount;

    if (order.instrument.instrumentCategory === InstrumentCategoryEnum.Futures) {
      if (!futuresValues.isLoaded) {
        return undefined;
      }

      return futuresValues.netValueSum + signedCommission;
    }

    if (!totalPrice.isLoaded) {
      return undefined;
    }

    return totalPrice.value + signedCommission;
  }, [calculatedCost, futuresValues, order.instrument.instrumentCategory, order.tradeType, totalPrice]);

  const reCalculateOrderCost = useCallback(() => {
    calculateOrderCost(order.instrument, order);
  }, [calculateOrderCost, order]);

  return (
    <div>
      {!isCostCalculated && (
        <NbLoader
          horizontalCenter
          verticalCenter
        />
      )}
      {isCostCalculated && calculatedCostState!.hasError && (
        <ErrorWidgetContent
          title={translation.orderConfirm.costCalculateError}
          onClick={reCalculateOrderCost}
        />
      )}
      {calculatedCostState && !calculatedCostState.hasError && !calculatedCostState.isLoading && calculatedCost && (
        <div className={classes.container}>
          {totalPrice.isLoaded && order.instrument.instrumentCategory !== InstrumentCategoryEnum.Futures && (
            <OrderConfirmRow
              label={translation.orderConfirm.rows.price}
              testIdBase="order-confirm-total-price"
              value={formatCurrency(totalPrice.value, order.instrument.currency)}
              isDark
            />
          )}
          {futuresValues.isLoaded && order.instrument.instrumentCategory === InstrumentCategoryEnum.Futures && (
            <OrderConfirmRow
              label={translation.orderConfirm.rows.futuresNetValue}
              testIdBase="order-confirm-futures-net-value"
              value={formatCurrency(futuresValues.netValueSum, order.instrument)}
              isDark
            />
          )}
          <OrderConfirmRow
            label={translation.orderConfirm.rows.commission}
            testIdBase="order-confirm-commission"
            value={formatCurrency(calculatedCost.amount, calculatedCost.currency)}
            isDark
          />
          <OrderConfirmRow
            label={translation.orderConfirm.rows.transactionSum}
            testIdBase="order-confirm-transaction-sum"
            value={
              isDefined(transactionSum) ? formatCurrency(transactionSum, order.instrument!.currency) : unknownValue
            }
            isDark
          />
        </div>
      )}
      {futuresValues.isLoaded && order.instrument.instrumentCategory === InstrumentCategoryEnum.Futures && (
        <OrderConfirmRow
          label={translation.orderConfirm.rows.futuresMarginRequirementSum}
          testIdBase="order-confirm-futures-margin-requirement-sum"
          value={formatCurrency(futuresValues.marginRequirementSum, order.instrument)}
        />
      )}
    </div>
  );
};
